import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminFetch } from '../utils/adminFetch';
import { getAdminCourseName } from '../utils/courseDisplay';
import {
  BATCH_STATUS_COLORS,
  BATCH_STATUS_LABELS,
  formatParisDateTime,
} from '../utils/studentMessageBatches';

type BatchRow = {
  id: string;
  courseId: string;
  courseTitle: string;
  courseProject?: string;
  status: string;
  scheduledAt: string;
  recipientCount: number;
  summary: { sent: number; failed: number; skipped: number } | null;
};

type CourseOption = {
  id: string;
  courseProject?: string;
  title: string | Record<string, string>;
};

const StudentMessageBatches: React.FC = () => {
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const res = await adminFetch(`${apiBase}/courses`);
        if (!res.ok) return;
        const data = await res.json();
        setCourses(
          (data || []).map((c: CourseOption & { _id?: string }) => ({
            id: c.id || c._id || '',
            courseProject: c.courseProject,
            title: c.title,
          }))
        );
      } catch {
        // ignore — le filtre formation reste vide
      }
    })();
  }, [apiBase]);

  const loadBatches = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (courseFilter) params.set('courseId', courseFilter);
      const res = await adminFetch(`${apiBase}/student-messages/batches?${params.toString()}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.error || data?.message || 'Impossible de charger les envois');
      }
      setBatches(data.batches || []);
    } catch (err: any) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, courseFilter]);

  const selectedCourse = useMemo(
    () => courses.find((course) => course.id === courseFilter) ?? null,
    [courses, courseFilter]
  );

  return (
    <Container maxWidth="lg">
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/student-messages')} sx={{ mb: 2 }}>
        Retour
      </Button>

      <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
        <Box>
          <Typography variant="h4" gutterBottom>
            Envois planifiés et historique
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suivez les campagnes programmées, annulez-les ou consultez les rapports d&apos;envoi.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: { xs: '100%', sm: 300 } }}
            options={courses}
            value={selectedCourse}
            onChange={(_event, value) => setCourseFilter(value?.id || '')}
            getOptionLabel={(option) => getAdminCourseName(option)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Formation"
                placeholder="Rechercher une formation…"
              />
            )}
            noOptionsText="Aucune formation trouvée"
          />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={statusFilter}
              label="Statut"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">Tous</MenuItem>
              <MenuItem value="scheduled">Planifiés</MenuItem>
              <MenuItem value="completed">Envoyés</MenuItem>
              <MenuItem value="cancelled">Annulés</MenuItem>
              <MenuItem value="failed">Échecs</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper variant="outlined">
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : batches.length === 0 ? (
          <Typography color="text.secondary" textAlign="center" py={6}>
            Aucun envoi pour le moment.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Formation</TableCell>
                  <TableCell>Date prévue</TableCell>
                  <TableCell>Destinataires</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell>Résultat</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow
                    key={batch.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/student-messages/batches/${batch.id}`)}
                  >
                    <TableCell>
                      {getAdminCourseName({
                        title: batch.courseTitle,
                        courseProject: batch.courseProject,
                      })}
                    </TableCell>
                    <TableCell>{formatParisDateTime(batch.scheduledAt)}</TableCell>
                    <TableCell>{batch.recipientCount}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={BATCH_STATUS_COLORS[batch.status] || 'default'}
                        label={BATCH_STATUS_LABELS[batch.status] || batch.status}
                      />
                    </TableCell>
                    <TableCell>
                      {batch.summary
                        ? `${batch.summary.sent} envoyé${batch.summary.sent > 1 ? 's' : ''}`
                        : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Container>
  );
};

export default StudentMessageBatches;
